/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';

import { Typography } from '@coursera/cds-core';

import type { DegreeListsEntry } from 'bundles/browse-common/legacy/types/degree';
import CollectionsSection, { condensedStyles } from 'bundles/browse/components/CollectionsSection';
import withDegreeCategoryCollections from 'bundles/unified-seo-content-common/hocs/withDegreeCategoryCollection';
import type { PropsFromWithDegreeCategoryCollection } from 'bundles/unified-seo-content-common/hocs/withDegreeCategoryCollection';
import {
  getFormattedCategoriesArray,
  getProductTypesArray,
} from 'bundles/unified-seo-content-common/utils/getFormattedQueryParameters';

const NUM_DEGREES_DISPLAYED = 6;

type PropsFromCaller = {
  degreeCategory: string | null;
  productType: string | null;
};

type PropsFromRouter = {
  isPreview: boolean;
};

type PropsToComponent = PropsFromCaller & PropsFromRouter & PropsFromWithDegreeCategoryCollection;

const styles = {
  editorErrorDiv: css`
    width: 100%;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 35px;
    background-color: var(--cds-color-feedback-background-error-weak);
    border-radius: 10px;
    margin: 20px 0;
  `,
  carouselHeaderText: css`
    h2.collection-name {
      text-transform: lowercase;

      &::first-letter {
        text-transform: uppercase;
      }
    }
  `,
};

const RecommendationCollection = ({
  isLoading,
  degreeListFromPremiumProductsCollection: degreeList,
  degreeCategory,
  productType,
  isPreview,
}: PropsToComponent) => {
  const degrees: DegreeListsEntry[] =
    degreeList?.slice(0, NUM_DEGREES_DISPLAYED)?.map((degree) => ({ degree, __typename: degree.__typename })) ?? [];
  const degreeCollection = { entries: degrees, __typename: '' };
  const allCollections = [degreeCollection];

  return (
    <div data-testid="recommendations-collection" css={[condensedStyles.loadingCarousel, styles.carouselHeaderText]}>
      {isPreview && !isLoading && !degreeCollection.entries.length && (
        <div css={styles.editorErrorDiv} data-testid="error-for-editor">
          <Typography variant="h1" color="error">
            {/* The following string will only be seen by article editors, so it should not need to be translated */}
            No degrees exist for the chosen combination of {productType} and {degreeCategory}.
          </Typography>
        </div>
      )}
      <CollectionsSection
        collections={allCollections}
        isDiscoveryCollectionsLoading={isLoading}
        shouldUseCondensedView={true}
      />
    </div>
  );
};

export const BaseComp = RecommendationCollection;

export default compose<PropsToComponent, PropsFromCaller>(
  withDegreeCategoryCollections(({ degreeCategory, productType }: PropsFromCaller) => {
    return {
      domains: null,
      categories: getFormattedCategoriesArray(degreeCategory),
      productVariants: getProductTypesArray(productType),
    };
  }, false),
  connectToRouter((router) => {
    const previewParam = router.location.query.preview;
    return {
      isPreview: Boolean(previewParam),
    };
  })
)(RecommendationCollection);
